import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { cityLinkProps, type LocationTarget } from "@/lib/locations";

interface Props {
  county: string;
  countySlug: string;
  regionSlug: string;
  currentCitySlug: string;
  cities: LocationTarget[];
}

/** Internal-link block shown on every city page beneath the FAQ and above
 *  the closing CTA. Links to every sibling city in the same county for
 *  topical clustering and PageRank distribution. Filter input appears once
 *  the list gets past a comfortable scan length. */
export function SiblingCities({ county, cities, currentCitySlug }: Props) {
  const siblings = cities.filter((c) => c.citySlug !== currentCitySlug);
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return siblings;
    return siblings.filter((c) => c.city.toLowerCase().includes(q));
  }, [query, siblings]);

  if (siblings.length === 0) return null;
  const showFilter = siblings.length >= 12;

  return (
    <section className="border-t border-border bg-muted/40 px-6 py-16 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-accent">
          More in {county}
        </p>
        <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-serif text-3xl leading-tight text-brand sm:text-4xl">
              HR support in nearby towns &amp; cities
            </h2>
            <p className="mt-3 max-w-2xl text-muted-foreground">
              We work with employers right across {county}. Explore how HRSC supports
              businesses in each of these locations.
            </p>
          </div>
          {showFilter && (
            <label className="relative block w-full sm:w-72">
              <span className="sr-only">Filter towns and cities in {county}</span>
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Filter ${siblings.length} locations…`}
                className="w-full min-h-11 rounded-md border border-border bg-background pl-9 pr-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </label>
          )}
        </div>
        {filtered.length === 0 ? (
          <p className="mt-8 text-sm text-muted-foreground">
            No towns match "{query}". Try a shorter phrase.
          </p>
        ) : (
          <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
            {filtered.map((c) => (
              <li key={c.citySlug}>
                <Link
                  {...cityLinkProps(c)}
                  className="group inline-flex items-baseline gap-2 text-sm text-brand hover:text-accent"
                >
                  <span className="font-medium underline-offset-4 group-hover:underline">
                    HR outsourcing in {c.city}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {c.tier}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
